import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
    AssessmentResults,
    CatalogSkill,
    GapAnalysisResult,
    GrikyAchievementsResult,
    GrikyAssessmentSession,
    GrikySkillObjective,
    GrikyUserSkill,
    LearningPathItem,
    LearningPathResult,
    UserLearningPath,
    UserLearningPathItem,
} from '../griky-skills.types';

@Injectable({
    providedIn: 'root',
})
export class GrikySkillsService {
    private readonly baseUrl = environment.envVar.API_URL;

    /**
     * Marca si ya se verificó el objetivo para el auto-modal de onboarding
     * durante esta sesión de la SPA. Al ser un servicio singleton, sobrevive
     * a la navegación entre secciones del módulo (clicks en el menú) y solo se
     * reinicia al recargar / volver a entrar a la vista.
     */
    onboardingChecked = false;

    constructor(private http: HttpClient) {}

    // ─── Assessment ────────────────────────────────────────────────────────

    startAssessment(companyId: number): Observable<GrikyAssessmentSession> {
        return this.http.post<GrikyAssessmentSession>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/assessment/start`,
            {}
        );
    }

    getAssessmentStatus(companyId: number): Observable<GrikyAssessmentSession> {
        return this.http.get<GrikyAssessmentSession>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/assessment/status`
        );
    }

    getAssessmentResults(companyId: number): Observable<AssessmentResults> {
        return this.http.get<AssessmentResults>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/assessment/results`
        );
    }

    retakeAssessment(companyId: number): Observable<GrikyAssessmentSession> {
        return this.http.post<GrikyAssessmentSession>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/assessment/retake`,
            {}
        );
    }

    // ─── Skills ────────────────────────────────────────────────────────────

    getUserSkills(companyId: number): Observable<GrikyUserSkill[]> {
        return this.http.get<GrikyUserSkill[]>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/skills`
        );
    }

    storeSkill(companyId: number, data: Partial<GrikyUserSkill>): Observable<GrikyUserSkill> {
        return this.http.post<GrikyUserSkill>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/skills`,
            data
        );
    }

    updateSkill(companyId: number, skillId: number, data: Partial<GrikyUserSkill>): Observable<GrikyUserSkill> {
        return this.http.put<GrikyUserSkill>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/skills/${skillId}`,
            data
        );
    }

    deleteSkill(companyId: number, skillId: number): Observable<void> {
        return this.http.delete<void>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/skills/${skillId}`
        );
    }

    // ─── Objectives ────────────────────────────────────────────────────────

    getObjective(companyId: number): Observable<GrikySkillObjective> {
        return this.http.get<GrikySkillObjective>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/objective`
        );
    }

    upsertObjective(companyId: number, data: GrikySkillObjective): Observable<GrikySkillObjective> {
        return this.http.post<GrikySkillObjective>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/objective`,
            data
        );
    }

    deleteObjective(companyId: number): Observable<void> {
        return this.http.delete<void>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/objective`
        );
    }

    // ─── Catalog ───────────────────────────────────────────────────────────────

    searchCatalogSkills(q: string): Observable<CatalogSkill[]> {
        return this.http.get<CatalogSkill[]>(
            `${this.baseUrl}/griky-skills/catalog/search`,
            { params: { q } }
        );
    }

    // ─── Gap Analysis & Learning Path ──────────────────────────────────────

    getGapAnalysis(companyId: number): Observable<GapAnalysisResult> {
        return this.http.get<GapAnalysisResult>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/gap-analysis`
        );
    }

    resetGapAnalysis(companyId: number): Observable<GapAnalysisResult> {
        return this.http.delete<GapAnalysisResult>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/gap-analysis`
        );
    }

    getLearningPath(companyId: number): Observable<LearningPathResult> {
        return this.http.get<LearningPathResult>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/learning-path`
        );
    }

    // ─── Rutas (User Learning Paths) ──────────────────────────────────────

    getLearningPathSuggestions(companyId: number): Observable<LearningPathResult> {
        return this.http.get<LearningPathResult>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/rutas/suggestions`
        );
    }

    getUserLearningPaths(companyId: number): Observable<UserLearningPath[]> {
        return this.http.get<UserLearningPath[]>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/rutas`
        );
    }

    saveUserLearningPath(companyId: number, items: LearningPathItem[]): Observable<UserLearningPath> {
        return this.http.post<UserLearningPath>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/rutas`,
            { items }
        );
    }

    renameLearningPath(companyId: number, pathId: number, name: string): Observable<{ id: number; name: string }> {
        return this.http.put<{ id: number; name: string }>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/rutas/${pathId}`,
            { name }
        );
    }

    deleteLearningPath(companyId: number, pathId: number): Observable<void> {
        return this.http.delete<void>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/rutas/${pathId}`
        );
    }

    updateLearningPathItem(
        companyId: number,
        pathId: number,
        itemId: number,
        completed: boolean
    ): Observable<UserLearningPathItem> {
        return this.http.patch<UserLearningPathItem>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/rutas/${pathId}/items/${itemId}`,
            { completed }
        );
    }

    // ─── Achievements ──────────────────────────────────────────────────────

    getAchievements(companyId: number): Observable<GrikyAchievementsResult> {
        return this.http.get<GrikyAchievementsResult>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/achievements`
        );
    }

    checkAchievements(companyId: number): Observable<{ unlocked: { key: string; name: string; icon: string; points: number }[] }> {
        return this.http.post<{ unlocked: { key: string; name: string; icon: string; points: number }[] }>(
            `${this.baseUrl}/griky-skills/companies/${companyId}/achievements/check`,
            {}
        );
    }

    // Nota: "compartir perfil" y "perfil público" ya no viven aquí — son
    // independientes de GrikySkills, ver LearnApi.shareProfile/getPublicProfile.

    // ─── Admin Settings ────────────────────────────────────────────────────

    getAdminSettings(companyId: number): Observable<{ grikySkillsModule: boolean }> {
        return this.http.get<{ grikySkillsModule: boolean }>(
            `${this.baseUrl}/admin/companies/${companyId}/griky-skills/settings`
        );
    }

    updateAdminSettings(companyId: number, enabled: boolean): Observable<{ grikySkillsModule: boolean }> {
        return this.http.put<{ grikySkillsModule: boolean }>(
            `${this.baseUrl}/admin/companies/${companyId}/griky-skills/settings`,
            { grikySkillsModule: enabled }
        );
    }
}
